/**
 * The way into the walkthrough, and the thing that starts it the first time.
 *
 * It stays on screen rather than appearing once and vanishing: a teacher uses this product
 * twice a week and will forget the shape of it, and someone who skipped the tour in a hurry
 * on Monday has no other way back to it.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useOptionalSession } from "../lib/session";
import { hasSeenTour, markTourSeen, startTour } from "../lib/tour";

/* Long enough for the dashboard behind it to have drawn, short enough not to feel broken. */
const FIRST_RUN_DELAY_MS = 900;

export function ShowMeAround() {
  const session = useOptionalSession();
  const { pathname } = useLocation();
  const started = useRef(false);

  const role = session?.user?.role;
  const isTeacher = role === "teacher";

  /*
   * Auto-start only on the dashboard, which is where signing in lands you. Starting it
   * wherever someone happens to be would yank a teacher out of a lesson they had opened
   * from a link, and the tour navigates away from the current screen by design.
   */
  useEffect(() => {
    if (!isTeacher || started.current || pathname !== "/teacher" || hasSeenTour()) return;
    started.current = true;
    /* Marked before it runs, not after: someone who closes it has still been offered it. */
    markTourSeen();
    const timer = window.setTimeout(() => void startTour(), FIRST_RUN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isTeacher, pathname]);

  if (!isTeacher) return null;

  return (
    <button
      type="button"
      className="util-tab"
      onClick={() => void startTour()}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
      </svg>
      Show me around
    </button>
  );
}
